import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { Footer } from "@/components/site/Footer";
import { SITE, transparencia, avisoNoOficial } from "@/config/site";

const H = ({ children }) => <h2 className="mt-10 text-[22px] font-bold text-[#0B3C5D]">{children}</h2>;
const P = ({ children }) => <p className="mt-3 text-[17px] leading-relaxed text-[#3A4753]">{children}</p>;

const Privacy = () => (
  <>
    <h1 className="text-[34px] font-extrabold leading-tight text-[#0B3C5D] sm:text-5xl">Política de privacidad</h1>
    <P>En {SITE.brand} cuidamos tus datos personales conforme a la Ley N° 19.628 sobre Protección de la Vida Privada y sus modificaciones, incluida la Ley N° 21.719.</P>
    <H>1. Responsable</H>
    <P>El responsable del tratamiento es {SITE.responsable}. Contacto: {SITE.email} · {SITE.whatsappDisplay}. {transparencia()}</P>
    <H>2. Qué datos recopilamos</H>
    <P>Solo los que tú nos entregas en el formulario o por WhatsApp: nombre, teléfono, correo (opcional, para enviarte una confirmación), si tienes Isapre o Fonasa, si buscas un plan individual o familiar y tu región. No solicitamos diagnósticos, enfermedades ni otros datos médicos.</P>
    <H>3. Para qué los usamos</H>
    <P>Únicamente para contactarte y entregarte la asesoría que solicitaste. La base legal es tu consentimiento, que otorgas al marcar la casilla del formulario.</P>
    <H>4. Con quién los compartimos</H>
    <P>No vendemos ni cedemos tus datos. Solo podrían compartirse con la Isapre que tú elijas, si decides avanzar con un plan y con tu autorización, o cuando la ley lo exija. Usamos proveedores tecnológicos (alojamiento web y correo) que tratan los datos por nuestra cuenta.</P>
    <H>5. Cuánto tiempo los guardamos</H>
    <P>Mientras sea necesario para la asesoría, y como máximo 24 meses desde tu último contacto, salvo que nos pidas eliminarlos antes.</P>
    <H>6. Tus derechos</H>
    <P>Puedes solicitar en cualquier momento el acceso, rectificación, supresión, oposición, portabilidad y bloqueo de tus datos, y retirar tu consentimiento, escribiendo a {SITE.email}. Responderemos dentro de los plazos legales.</P>
    <H>7. Cookies</H>
    <P>Usamos cookies necesarias para el funcionamiento del sitio. Solo si las aceptas, se pueden activar herramientas de medición (Google Analytics o Meta Pixel). Puedes cambiar tu elección borrando las cookies de tu navegador.</P>
    <H>8. Seguridad</H>
    <P>Aplicamos medidas razonables para proteger tus datos frente a accesos no autorizados, pérdida o alteración.</P>
  </>
);

const Terms = () => (
  <>
    <h1 className="text-[34px] font-extrabold leading-tight text-[#0B3C5D] sm:text-5xl">Términos y condiciones</h1>
    <H>1. Sobre este sitio</H>
    <P>{SITE.brand} entrega orientación sobre planes de salud de Isapre. {transparencia()} {avisoNoOficial}</P>
    <H>2. Naturaleza de la asesoría</H>
    <P>La asesoría no tiene costo para ti. La información publicada es referencial: las condiciones, coberturas y precios dependen de cada plan y de tu situación particular. La contratación de un plan se rige por el contrato de salud que firmes con la Isapre correspondiente.</P>
    <H>3. Sin garantías de resultado</H>
    <P>No garantizamos ahorros, precios ni la aceptación de una Isapre, ya que estas dependen de evaluaciones propias de cada institución. La decisión final siempre es tuya.</P>
    <H>4. Calculadora del 7%</H>
    <P>El cálculo del 7% es aproximado y no aplica automáticamente el tope imponible legal ni otras situaciones particulares.</P>
    <H>5. Uso de datos</H>
    <P>El tratamiento de tus datos se rige por nuestra <Link to="/privacidad" className="font-semibold text-[#0B3C5D] underline">Política de privacidad</Link>.</P>
    <H>6. Legislación aplicable</H>
    <P>Estos términos se rigen por las leyes de la República de Chile, incluida la Ley N° 19.496 sobre Protección de los Derechos de los Consumidores.</P>
  </>
);

export default function Legal({ type }) {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `${type === "privacidad" ? "Política de privacidad" : "Términos y condiciones"} · ${SITE.brand}`;
  }, [type]);
  return (
    <>
      <header className="border-b border-[#E3EAEF] bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3 sm:px-6">
          <Link to="/" aria-label="Volver al inicio"><Logo /></Link>
          <Link to="/" className="inline-flex items-center gap-2 text-[16px] font-semibold text-[#0B3C5D]" data-testid="legal-back-link"><ArrowLeft className="h-5 w-5" /> Volver</Link>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16" data-testid={`legal-${type}`}>
        {type === "privacidad" ? <Privacy /> : <Terms />}
        <p className="mt-10 text-[15px] text-[#4A5864]">Última actualización: noviembre de 2026.</p>
      </main>
      <Footer />
    </>
  );
}
